import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  getSiteFlowConditionTypeOptions,
  getSiteFlowTagOptions,
} from '../../domain/condition_families/siteFlowConditionFamily';
import {
  buildDefaultWhyThisMattersText,
  buildDiscouragedLanguageWarning,
  defaultWorkerSupportNoteEnabled,
} from '../../domain/reportLanguage/reportLanguageGuardrails';
import {
  buildDefaultConditionSummary,
  readWorkplaceCondition,
  type WorkplaceConditionFields,
} from '../../domain/workplaceCondition/workplaceCondition';
import { reportsApi } from '../../services/reportsApi';
import type { SafetyCaseRecord } from '../../services/reportsApi';
import ReportLanguagePreviewPanel from '../safetyReports/ReportLanguagePreviewPanel';
import WorkplaceConditionContextRows from '../safetyReports/WorkplaceConditionContextRows';
import { buildWorkplaceConditionContextFromSafetyCase } from '../../domain/workplaceCondition/workplaceConditionContext';
import { resolveInvestigationAlignmentFromSafetyCase } from '../../domain/reportContext/investigationAlignment';
import { resolveClaimsRoiFromSafetyCase } from '../../domain/reportContext/claimsRoiAlignment';
import { resolveSafetyControlReviewFromSafetyCase } from '../../domain/reportContext/safetyControlReview';
import InvestigationAlignmentCard from '../safetyReports/InvestigationAlignmentCard';
import ClaimsRoiAlignmentCard from '../safetyReports/ClaimsRoiAlignmentCard';
import SafetyControlReviewCard from '../safetyReports/SafetyControlReviewCard';
import type { ContributingConditionPreview } from './ContributingConditionReviewSection';
import {
  type SafetyCaseAttestationSaveHandler,
  useRegisterSafetyCaseSaveHandler,
} from '../../domain/safetyCases/safetyCaseAttestationSave';

type WorkplaceConditionSummaryCardProps = {
  safetyCaseId: string;
  item: SafetyCaseRecord;
  workflowSummary?: Record<string, unknown> | null;
  contributingPreview?: ContributingConditionPreview;
  onSaved?: (fields: WorkplaceConditionFields) => void;
  hideSaveButton?: boolean;
  saveHandlerRef?: React.MutableRefObject<SafetyCaseAttestationSaveHandler | null>;
};

function fieldsFromItem(item: SafetyCaseRecord): WorkplaceConditionFields {
  return readWorkplaceCondition(item.scope);
}

export const WorkplaceConditionSummaryCard: React.FC<WorkplaceConditionSummaryCardProps> = ({
  safetyCaseId,
  item,
  workflowSummary,
  contributingPreview,
  onSaved,
  hideSaveButton = false,
  saveHandlerRef,
}) => {
  const conditionTypeOptions = useMemo(() => getSiteFlowConditionTypeOptions(), []);
  const tagOptions = useMemo(() => getSiteFlowTagOptions(), []);
  const [conditionType, setConditionType] = useState('');
  const [conditionTags, setConditionTags] = useState<string[]>([]);
  const [conditionSummary, setConditionSummary] = useState('');
  const [whyThisMattersText, setWhyThisMattersText] = useState('');
  const [workerSupportNoteEnabled, setWorkerSupportNoteEnabled] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const defaultSummary = useMemo(
    () => buildDefaultConditionSummary({ scope: item.scope || undefined }),
    [item.scope],
  );

  useEffect(() => {
    const fields = fieldsFromItem(item);
    const nextConditionType = String(fields.condition_type || '').trim();
    setConditionType(nextConditionType);
    setConditionTags(Array.isArray(fields.condition_tags) ? fields.condition_tags.filter(Boolean) : []);
    setConditionSummary(String(fields.condition_summary || '').trim() || defaultSummary);
    setWhyThisMattersText(
      String(fields.why_this_matters_text || '').trim()
      || (nextConditionType ? buildDefaultWhyThisMattersText(nextConditionType) : ''),
    );
    setWorkerSupportNoteEnabled(
      fields.worker_support_note_enabled === false
        ? false
        : defaultWorkerSupportNoteEnabled({ sharedOrExport: true }),
    );
    setSaveError(null);
    setSaveNotice(null);
  }, [defaultSummary, item]);

  const conditionSummaryWarning = useMemo(
    () => buildDiscouragedLanguageWarning(conditionSummary),
    [conditionSummary],
  );

  const savedReviewFields = useMemo(() => fieldsFromItem(item), [item]);
  const previewContributing = useMemo(
    () => contributingPreview || {
      likelyContributingCondition: String(savedReviewFields.likely_contributing_condition || '').trim(),
      contributingConditionStatus: (
        String(savedReviewFields.contributing_condition_status || '').trim() || ''
      ) as ContributingConditionPreview['contributingConditionStatus'],
    },
    [contributingPreview, savedReviewFields],
  );

  const handleConditionTypeChange = (nextConditionType: string) => {
    setConditionType(nextConditionType);
    if (!whyThisMattersText.trim() && nextConditionType.trim()) {
      setWhyThisMattersText(buildDefaultWhyThisMattersText(nextConditionType));
    }
  };

  const selectedTagOptions = useMemo(
    () => tagOptions.filter((option) => conditionTags.includes(option.id)),
    [conditionTags, tagOptions],
  );
  const conditionContext = useMemo(
    () => buildWorkplaceConditionContextFromSafetyCase(item, workflowSummary),
    [item, workflowSummary],
  );
  const investigationAlignment = useMemo(
    () => resolveInvestigationAlignmentFromSafetyCase(item),
    [item],
  );
  const claimsRoiAlignment = useMemo(
    () => resolveClaimsRoiFromSafetyCase(item),
    [item],
  );
  const safetyControlReview = useMemo(
    () => resolveSafetyControlReviewFromSafetyCase(item),
    [item],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    if (!hideSaveButton) setSaveNotice(null);
    try {
      const response = await reportsApi.patchWorkplaceCondition(safetyCaseId, {
        condition_type: conditionType.trim() || null,
        condition_summary: conditionSummary.trim() || null,
        condition_tags: conditionTags,
        why_this_matters_text: whyThisMattersText.trim() || null,
        worker_support_note_enabled: workerSupportNoteEnabled,
      });
      const saved = readWorkplaceCondition(response.safety_case?.scope || response.finding?.scope);
      onSaved?.(saved);
      if (!hideSaveButton) setSaveNotice('Workplace condition saved.');
    } catch (error: any) {
      setSaveError(error?.message || 'Unable to save workplace condition right now.');
      throw error;
    } finally {
      setSaving(false);
    }
  }, [
    conditionSummary,
    conditionTags,
    conditionType,
    hideSaveButton,
    onSaved,
    safetyCaseId,
    whyThisMattersText,
    workerSupportNoteEnabled,
  ]);

  useRegisterSafetyCaseSaveHandler(saveHandlerRef, handleSave);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }} data-testid="workplace-condition-summary-card">
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
          Workplace Condition Summary
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Document the recurring workplace condition under review. Generated summaries describe observed exposure only.
        </Typography>
        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="workplace-condition-type-label">Condition type</InputLabel>
            <Select
              labelId="workplace-condition-type-label"
              label="Condition type"
              value={conditionType}
              onChange={(event) => handleConditionTypeChange(String(event.target.value || ''))}
            >
              <MenuItem value="">
                <em>Select condition type</em>
              </MenuItem>
              {conditionTypeOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            options={tagOptions}
            value={selectedTagOptions}
            onChange={(_event, value) => setConditionTags(value.map((option) => option.id))}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(left, right) => left.id === right.id}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id} size="small" label={option.label} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Condition tags" placeholder="Select site-flow tags" size="small" />
            )}
          />
          <TextField
            label="Condition summary"
            value={conditionSummary}
            onChange={(event) => setConditionSummary(event.target.value)}
            fullWidth
            multiline
            minRows={2}
            size="small"
            helperText="Neutral what / where / when summary. Edit if the default wording needs adjustment."
          />
          {conditionSummaryWarning ? (
            <Alert severity="warning" data-testid="condition-summary-language-warning">
              {conditionSummaryWarning}
            </Alert>
          ) : null}
          <ReportLanguagePreviewPanel
            conditionSummary={conditionSummary}
            whyThisMattersText={whyThisMattersText}
            likelyContributingCondition={previewContributing.likelyContributingCondition}
            contributingConditionStatus={previewContributing.contributingConditionStatus}
            workerSupportNoteEnabled={workerSupportNoteEnabled}
            onWhyThisMattersTextChange={setWhyThisMattersText}
            onWorkerSupportNoteEnabledChange={setWorkerSupportNoteEnabled}
          />
          {saveError ? <Alert severity="error">{saveError}</Alert> : null}
          {saveNotice ? <Alert severity="success">{saveNotice}</Alert> : null}
          {!hideSaveButton ? (
            <Box>
              <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : 'Save workplace condition'}
              </Button>
            </Box>
          ) : null}
          <WorkplaceConditionContextRows context={conditionContext} />
          <InvestigationAlignmentCard
            alignment={investigationAlignment}
            compact
            title="Investigation alignment"
            testId="workplace-condition-investigation-alignment"
          />
          <ClaimsRoiAlignmentCard
            alignment={claimsRoiAlignment}
            compact
            title="Claims / ROI alignment"
            testId="workplace-condition-claims-roi"
          />
          <SafetyControlReviewCard
            alignment={safetyControlReview}
            compact
            title="Safety control review"
            testId="workplace-condition-safety-control-review"
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default WorkplaceConditionSummaryCard;
